import { useCentralizedMe } from '@crehana/auth';
import {
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
} from '@crehana/compass.ui/v3/tabs';
import { Textlink } from '@crehana/compass.ui/v3/textlink';
import { useRBAC } from '@crehana/rbac/hooks/use-rbac';

import { useTeamLastActivity } from '../hooks/use-team-last-activity';
import { useTranslation } from '../i18n';
import type { TWidgetSize } from '../types';
import { WidgetContainer } from './containers';
import { WidgetTeam } from './widget-team';

type TWidgetWithTabsProps = {
  children: React.ReactNode;
  timeZone: string;
  size: TWidgetSize;
};

const DEFAULT_TAB_VALUE = 'personal';
/** Page-level wrapper around the core Widget.
 * Leaders see tabs ("Mi asistencia" / "Mi equipo") with team count badge.
 * Non-leaders see a plain title + subtitle header.
 * "Mi equipo" tab content is a placeholder — team view is a future feature. */
export const WidgetWithTabs = ({
  children,
  timeZone,
  size,
}: TWidgetWithTabsProps) => {
  const { t } = useTranslation();
  const { currentPageOrganization } = useCentralizedMe();

  const moduleUrl = `/org/${currentPageOrganization.slug}/attendance/tracker/personal/`;
  const { totalCount: teamCount } = useTeamLastActivity();

  const { isLeader } = useRBAC();
  const shouldRenderTabs = isLeader();

  const linkViewModule = (
    <Textlink asChild size="sm" className="tw-hidden sm:tw-block">
      <a href={moduleUrl}>{t('widget_header_link_view_module')}</a>
    </Textlink>
  );

  if (shouldRenderTabs) {
    return (
      <WidgetContainer mode="widget">
        <Tabs
          defaultValue={DEFAULT_TAB_VALUE}
          className="tw-h-full tw-flex tw-flex-col"
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <TabsList variant="secondary" size="sm">
              <TabsTrigger value="personal">
                {t('widget_header_tabs_personal')}
              </TabsTrigger>
              <TabsTrigger value="team">
                {t('widget_header_tabs_team')}
                <TabsCount>{teamCount}</TabsCount>
              </TabsTrigger>
            </TabsList>

            {linkViewModule}
          </div>

          <TabsContent
            value="personal"
            className="tw-mt-1.2 tw-flex-1 tw-min-h-0"
          >
            {children}
          </TabsContent>

          <TabsContent value="team" className="tw-mt-1.2 tw-flex-1 tw-min-h-0">
            <WidgetTeam timeZone={timeZone} size={size} />
          </TabsContent>
        </Tabs>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer mode="widget">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.2">
        <div className="tw-space-y-0.4">
          <h2 className="tw-cds-font-subtitle-01">
            {t('widget_no_leader_title')}
          </h2>
          <p className="tw-cds-font-body-01 tw-cds-text-subtle">
            {t('widget_no_leader_subtitle')}
          </p>
        </div>

        <div>{linkViewModule}</div>
      </div>
      {children}
    </WidgetContainer>
  );
};
